import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { formatCurrency } from "../../utils/currencyUtils";
import { useCurrency } from "../../hooks/useCurrency";
import toast from "react-hot-toast";
import { CheckCircle, Clock, ShieldCheck, RefreshCw, RotateCcw, Building2, Wallet, X } from "lucide-react";
import {
    get_order,
    get_order_escrow_status,
    confirm_delivery,
    messageClear
} from "../../store/reducers/orderReducer";
import {
    create_return_request,
    messageClear as returnMessageClear
} from "../../store/reducers/returnReducer";

const ESCROW_BADGE = {
    held: { label: 'Funds Secured', color: 'bg-blue-100 text-blue-700', icon: <ShieldCheck size={12} /> },
    released: { label: 'Funds Released', color: 'bg-green-100 text-green-700', icon: <CheckCircle size={12} /> },
    refunded: { label: 'Refunded', color: 'bg-red-100 text-red-700', icon: null },
    disputed: { label: 'Disputed', color: 'bg-yellow-100 text-yellow-700', icon: null },
}

const RETURN_REASONS = [
    { value: 'defective', label: 'Defective / Not Working' },
    { value: 'wrong_item', label: 'Wrong Item Received' },
    { value: 'not_as_described', label: 'Not as Described' },
    { value: 'damaged_in_shipping', label: 'Damaged in Shipping' },
    { value: 'changed_mind', label: 'Changed My Mind' },
    { value: 'other', label: 'Other' },
]

const ReturnModal = ({ orderId, product, onClose, onSubmit, submitting }) => {
    const [reason, setReason] = useState('')
    const [reasonDetail, setReasonDetail] = useState('')
    const [refundType, setRefundType] = useState('bank')
    const [bankName, setBankName] = useState('')
    const [accountNumber, setAccountNumber] = useState('')
    const [accountName, setAccountName] = useState('')
    const [paypalEmail, setPaypalEmail] = useState('')

    const handleSubmit = (e) => {
        e.preventDefault()
        const refundAccountInfo = refundType === 'bank'
            ? { type: 'bank', bankName, accountNumber, accountName }
            : { type: 'paypal', paypalEmail }
        onSubmit({ orderId, product: { productId: product._id }, reason, reasonDetail, refundAccountInfo })
    }

    return (
        <>
            <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
            <div className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-gray-900">Request a Return</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
                    <img src={product.images?.[0]} alt="" className="w-12 h-12 object-cover rounded-md" />
                    <div>
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        <div className="text-xs text-gray-500">Qty: {product.quantity}</div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Reason for Return *</label>
                        <select
                            required
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">Select a reason...</option>
                            {RETURN_REASONS.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Additional Details (optional)</label>
                        <textarea
                            value={reasonDetail}
                            onChange={e => setReasonDetail(e.target.value)}
                            rows={2}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                            placeholder="Describe the issue..."
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">Refund Method *</label>
                        <div className="flex gap-2 mb-3">
                            <button
                                type="button"
                                onClick={() => setRefundType('bank')}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded-lg border transition-colors ${
                                    refundType === 'bank' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <Building2 size={14} /> Bank Transfer
                            </button>
                            <button
                                type="button"
                                onClick={() => setRefundType('paypal')}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded-lg border transition-colors ${
                                    refundType === 'paypal' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <Wallet size={14} /> PayPal
                            </button>
                        </div>

                        {refundType === 'bank' && (
                            <div className="space-y-2">
                                <input required value={bankName} onChange={e => setBankName(e.target.value)} placeholder="Bank Name *" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                <input required value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="Account Number *" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                <input required value={accountName} onChange={e => setAccountName(e.target.value)} placeholder="Account Name *" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                            </div>
                        )}
                        {refundType === 'paypal' && (
                            <input required type="email" value={paypalEmail} onChange={e => setPaypalEmail(e.target.value)} placeholder="PayPal Email *" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        )}
                    </div>

                    <div className="flex gap-2 pt-1">
                        <button type="button" onClick={onClose} className="flex-1 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={submitting} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-60">
                            {submitting ? 'Submitting...' : 'Submit Request'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    )
}

const Order = () => {
    const { orderId } = useParams();
    const dispatch = useDispatch();
    const { myOrder, sellerOrders, escrowStatuses, confirmingDelivery, successMessage, errorMessage } = useSelector((state) => state.order);
    const { userInfo } = useSelector((state) => state.auth);
    const { currencyCode, exchangeLoaded } = useCurrency();
    const { successMessage: returnSuccess, errorMessage: returnError } = useSelector((state) => state.return);

    const [returnModal, setReturnModal] = useState(null)
    const [submittingReturn, setSubmittingReturn] = useState(false)

    useEffect(() => {
        dispatch(get_order(orderId));
        dispatch(get_order_escrow_status(orderId));
    }, [orderId]);

    useEffect(() => {
        if (successMessage) { toast.success(successMessage); dispatch(messageClear()); }
        if (errorMessage) { toast.error(errorMessage); dispatch(messageClear()); }
    }, [successMessage, errorMessage]);

    useEffect(() => {
        if (returnSuccess) {
            toast.success(returnSuccess)
            dispatch(returnMessageClear())
            setReturnModal(null)
            setSubmittingReturn(false)
        }
        if (returnError) {
            toast.error(returnError)
            dispatch(returnMessageClear())
            setSubmittingReturn(false)
        }
    }, [returnSuccess, returnError]);

    const getEscrowForSeller = (sellerOrderId) =>
        escrowStatuses.find(e => e.sellerOrderId === sellerOrderId?.toString())

    const handleConfirmReceipt = (sellerOrderId) => {
        dispatch(confirm_delivery({ orderId, sellerOrderId }))
    }

    const handleReturnSubmit = (payload) => {
        setSubmittingReturn(true)
        dispatch(create_return_request(payload))
    }

    const OrderHeader = () => (
        <div className="pb-4">
            <h2 className="text-gray-900 font-semibold flex items-center gap-2">
                <span>Order #{myOrder._id}</span>
                <span className="text-muted-foreground text-sm">{myOrder.date}</span>
            </h2>
        </div>
    );

    const CustomerInfo = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div className="space-y-3">
                <h2 className="text-gray-900 font-semibold">Delivery Information</h2>
                <div className="space-y-2">
                    <p className="text-gray-700">{myOrder.shippingInfo?.name}</p>
                    <div className="flex items-center gap-2">
                        <span className="bg-[#FFE11B] text-black text-xs font-medium px-3 py-1 rounded">Home</span>
                        <span className="text-muted-foreground text-sm">
                            {myOrder.shippingInfo?.address}, {myOrder.shippingInfo?.province},{" "}
                            {myOrder.shippingInfo?.city}, {myOrder.shippingInfo?.area}
                        </span>
                    </div>
                    <p className="text-muted-foreground text-sm">Email: {userInfo.email}</p>
                </div>
            </div>
            <div className="space-y-3">
                <h2 className="text-gray-900 font-semibold">Order Details</h2>
                <div className="space-y-2">
                    <p className="text-gray-700">Total Price: {formatCurrency(myOrder.price, currencyCode, exchangeLoaded)} shipping included.</p>
                    <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Payment Status:</span>
                        <span className={`py-1 text-xs px-3 rounded-md ${myOrder.payment_status === "paid" ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
                            {myOrder.payment_status}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const ProductList = () => (
        <div className="mt-6">
            <h2 className="text-gray-900 text-lg font-semibold mb-4">Products</h2>
            <div className="space-y-6">
                {sellerOrders?.map((sellerOrder, index) => (
                    <SellerOrderSection
                        key={index}
                        sellerOrder={sellerOrder}
                        products={myOrder.products?.filter(p => p.sellerId === sellerOrder.sellerId)}
                    />
                ))}
            </div>
        </div>
    );

    const SellerOrderSection = ({ sellerOrder, products }) => {
        const escrow = getEscrowForSeller(sellerOrder._id)
        const badge = escrow ? ESCROW_BADGE[escrow.status] : null
        const isDelivered = sellerOrder.delivery_status === 'delivered' || sellerOrder.delivery_status === 'Delivered'
        const canConfirm = isDelivered && escrow?.status === 'held'
        const confirming = confirmingDelivery[sellerOrder._id?.toString()]

        return (
            <div className="">
                <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                    <div className="space-y-1">
                        <h3 className="text-gray-900 font-medium">{products?.[0]?.shopName}</h3>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-muted-foreground text-sm">Delivery Status:</span>
                            <span className={`py-1 text-xs px-3 rounded-md ${
                                sellerOrder.delivery_status === "Delivered" || sellerOrder.delivery_status === "delivered"
                                    ? "bg-green-600 text-white"
                                    : sellerOrder.delivery_status === "Processing"
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-black border border-gray-200"
                            }`}>
                                {sellerOrder.delivery_status}
                            </span>
                            {badge && (
                                <span className={`flex items-center gap-1 py-1 text-xs px-3 rounded-md ${badge.color}`}>
                                    {badge.icon}
                                    {badge.label}
                                </span>
                            )}
                        </div>
                    </div>

                    {canConfirm && (
                        <button
                            onClick={() => handleConfirmReceipt(sellerOrder._id)}
                            disabled={confirming}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-60"
                        >
                            {confirming
                                ? <><RefreshCw size={13} className="animate-spin" /> Confirming...</>
                                : <><CheckCircle size={13} /> Confirm Receipt</>
                            }
                        </button>
                    )}

                    {escrow?.status === 'released' && (
                        <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                            <CheckCircle size={13} /> Payment released to seller
                        </span>
                    )}

                    {escrow?.autoReleaseAt && escrow.status === 'held' && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock size={12} />
                            Auto-release: {new Date(escrow.autoReleaseAt).toLocaleDateString()}
                        </span>
                    )}
                </div>

                <div className="space-y-4">
                    {products?.map((product, index) => (
                        <ProductCard key={index} product={product} isPaid={myOrder.payment_status === 'paid'} />
                    ))}
                </div>
            </div>
        );
    };

    const ProductCard = ({ product, isPaid }) => (
        <div className="rounded-lg">
            <div className="flex justify-between items-center">
                <div className="flex gap-4">
                    <img className="w-16 h-16 rounded-md object-cover" src={product.images[0]} alt={product.name} />
                    <div className="flex flex-col justify-center">
                        <Link className="text-gray-900 hover:text-blue-600 transition-colors">{product.name}</Link>
                        <div className="text-sm text-muted-foreground space-y-1">
                            <p>Brand: {product.brand}</p>
                            <p>Quantity: {product.quantity}</p>
                        </div>
                        {isPaid && (
                            <button
                                onClick={() => setReturnModal(product)}
                                className="mt-2 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 transition-colors w-fit"
                            >
                                <RotateCcw size={11} /> Request Return
                            </button>
                        )}
                    </div>
                </div>
                <div className="text-right">
                    <h2 className="text-gray-900 font-semibold">
                        {formatCurrency(product.price - Math.floor((product.price * product.discount) / 100), currencyCode, exchangeLoaded)}
                    </h2>
                    <p className="text-muted-foreground text-sm line-through">{formatCurrency(product.price, currencyCode, exchangeLoaded)}</p>
                    <p className="text-green-400 text-sm">-{product.discount}%</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-white border border-gray-100 shadow-sm rounded-lg p-6">
            <OrderHeader />
            <CustomerInfo />
            <ProductList />
            {returnModal && (
                <ReturnModal
                    orderId={orderId}
                    product={returnModal}
                    onClose={() => setReturnModal(null)}
                    onSubmit={handleReturnSubmit}
                    submitting={submittingReturn}
                />
            )}
        </div>
    );
};

export default Order;
